import type {
  ApiResponseState,
  ApiRequestMethod,
  ApiRequestHeaders,
  ApiRequestOptions,
} from "./client.types";

import { headersBase } from "./headers";
import { REQUEST_TIMEOUT_MS } from "./config";
import { ResponseStatusCodes } from "./responseStatusCodes";

async function fetchWithTimeout(requestURL: string, params: {}, timeout: number = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutID = setTimeout(() => controller.abort(), timeout);

  const response = await fetch(requestURL, {
    ...params,
    signal: controller.signal,
  });
  clearTimeout(timeoutID);
  return response;
}

export async function apiRequest<T, B = unknown>(
  requestURL: URL,
  method: ApiRequestMethod,
  body: B,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
  timeout?: number
): Promise<ApiResponseState<T>> {
  const requestParameters: { [key: string]: any } = {
    method,
    headers: headersBase(!!options?.webview, headers),
  };

  if (method !== "GET" && method !== "HEAD") {
    requestParameters.body =
      body instanceof FormData ? body : JSON.stringify(body);
  }

  const state: ApiResponseState<T> = {
    isOk: false,
    isError: false,
    isAuthError: false,
    isInvalid: false,
    data: undefined,
    error: undefined,
    responseStatus: undefined,
  };

  try {
    const response = await fetchWithTimeout(requestURL.href, requestParameters, timeout);
    state.responseStatus = response.status;
    if (!response.ok)
      throw { code: response.status, message: response.statusText };
    const result = await response.json();
    if (result.error) {
      state.isInvalid = true;
      throw result.error;
    }
    state.isOk = true;
    state.data = result;
  } catch (error: any) {
    state.error = error;
    state.isError = true;
    state.isAuthError = error.code === ResponseStatusCodes.UNAUTHORIZED;
  } finally {
    return Object.freeze(state);
  }
}
